import { MoreHoriz } from '@mui/icons-material';
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { CalculsGlobal } from '@norma/shared/constants/calculs';
import {
  ListeProjectionAgrement,
  ListeProjectionAgrementRow,
  NumeroPieceValuesRow,
  PrejudiceFormListeProjection,
} from '@norma/shared/types/prejudice.type';
import React, { useState } from 'react';
import { Control, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ExpandableTextFieldForm } from 'src/components/forms/ExpandableTextFieldForm';
import { validationSchemaNumeroPieces } from 'src/constants/prejudice/validation';
import { useFormArrayUtils } from 'src/hooks/useFormArrayUtils';
import * as yup from 'yup';
import { ComputedReadFieldForm } from '../../../forms/ComputedReadFieldForm';
import { TextFieldForm } from '../../../forms/TextFieldForm';
import { IMaskNumberProps, MaskNumber } from '../../../masks/MaskNumber';
import { NormalTable } from '../../../styled';
import { FormNumeroPieceDialog } from '../FormNumeroPieceDialog';

interface Props {
  control: Control<PrejudiceFormListeProjection>;
  allNumerosPieces: NumeroPieceValuesRow[];
}

export const validationSchemaPrejudiceAgrement =
  (): yup.ObjectSchema<ListeProjectionAgrement> =>
    yup.object({
      rows: yup
        .array()
        .defined()
        .of(
          yup.object({
            numerosPieces: validationSchemaNumeroPieces,
            typePrejudice: yup.string().required(),
            montant: yup.number().required().min(0),
          }),
        ),
    });
const initialValuesRow: ListeProjectionAgrementRow = {
  typePrejudice: '',
  montant: 0,
  numerosPieces: { rows: [] },
};

export const PrejudiceAgrement: React.FC<Props> = ({
  control,
  allNumerosPieces,
}) => {
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);

  const handleMenuOpen =
    (index: number) => (event: React.MouseEvent<HTMLElement>) => {
      setOpenMenuIndex(index);
      setAnchorEl(event.currentTarget);
    };

  const handleMenuClose = () => {
    setOpenMenuIndex(null);
    setAnchorEl(null);
  };
  const { duplicate, remove, fields, append } = useFormArrayUtils({
    control,
    name: 'prejudiceValues.rows',
    getValues,
    setValue,
  });

  const handleDelete = (index: number) => {
    remove(index);
    handleMenuClose();
  };

  const handleDuplicate = (index: number) => {
    duplicate(index);
    handleMenuClose();
  };

  return (
    <>
      <TableContainer component={Paper}>
        <NormalTable size="medium">
          <TableHead>
            <TableRow>
              <TableCell> {t('numeroPiece.form.columnHeader')}</TableCell>
              <TableCell>
                {t(
                  'prejudice.prejudicesFormTypes.LISTE_PROJECTION.PREJUDICE_AGREMENT.fields.rows.typePrejudice.columnHeader',
                )}
              </TableCell>
              <TableCell align="center">
                {t(
                  'prejudice.prejudicesFormTypes.LISTE_PROJECTION.PREJUDICE_AGREMENT.fields.rows.montant.columnHeader',
                )}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map((field, index) => (
              <TableRow key={field.id}>
                <TableCell>
                  <FormNumeroPieceDialog
                    control={control}
                    name={`prejudiceValues.rows.${index}.numerosPieces`}
                    allOtherNumeroPieces={allNumerosPieces}
                  />
                </TableCell>
                <TableCell align="center">
                  <ExpandableTextFieldForm
                    control={control}
                    name={`prejudiceValues.rows.${index}.typePrejudice`}
                    sx={{ width: '100%' }}
                    dialogTitle={t(
                      'prejudice.prejudicesFormTypes.LISTE_PROJECTION.PREJUDICE_AGREMENT.fields.rows.typePrejudice.columnHeader',
                    )}
                  />
                </TableCell>
                <TableCell align="center">
                  <TextFieldForm
                    control={control}
                    name={`prejudiceValues.rows.${index}.montant`}
                    InputProps={{
                      inputComponent: MaskNumber as any,
                      inputProps: {
                        numberMask: { scale: 2 },
                        suffix: '€',
                      } as IMaskNumberProps,
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton onClick={handleMenuOpen(index)}>
                    <MoreHoriz />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={openMenuIndex === index}
                    onClose={handleMenuClose}
                  >
                    <MenuItem onClick={() => handleDelete(index)}>
                      {t('forms.table.deleteRow', { index: index + 1 })}
                    </MenuItem>
                    <MenuItem onClick={() => handleDuplicate(index)}>
                      {t('forms.table.duplicateRow', { index: index + 1 })}
                    </MenuItem>
                  </Menu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </NormalTable>
      </TableContainer>
      <ComputedReadFieldForm
        name="montantTotal"
        control={control}
        watchFields={['prejudiceValues.rows']}
        compute={([rows]) => {
          return CalculsGlobal.sommeMontantRows(rows);
        }}
      />
      <Button
        sx={{ alignSelf: 'center' }}
        onClick={() => {
          append(initialValuesRow);
        }}
      >
        Ajouter une ligne
      </Button>
    </>
  );
};
